import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Clock, X, Filter, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const DecisionLogPage = ({ user }) => {
  const navigate = useNavigate();
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    loadDecisions();
  }, [user, filter]);

  const loadDecisions = async () => {
    try {
      const url = filter === 'ALL'
        ? `${process.env.REACT_APP_BACKEND_URL}/api/decisions`
        : `${process.env.REACT_APP_BACKEND_URL}/api/decisions?filter_by=${filter}`;

      const response = await fetch(url, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setDecisions(data);
      } else {
        toast.error('Failed to load decisions');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const getDecisionIcon = (decision) => {
    switch (decision) {
      case 'YES':
        return <Check className="w-5 h-5 text-turquoise-500" />;
      case 'WAIT':
        return <Clock className="w-5 h-5 text-purple-500" />;
      case 'NO':
        return <X className="w-5 h-5 text-pink-500" />;
      default:
        return null;
    }
  };

  const getDecisionColor = (decision) => {
    switch (decision) {
      case 'YES':
        return 'border-turquoise-300 bg-turquoise-50';
      case 'WAIT':
        return 'border-purple-300 bg-purple-50';
      case 'NO':
        return 'border-pink-300 bg-pink-50';
      default:
        return 'border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-8 fade-in">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 hover:text-purple-600 mb-4 transition-colors"
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          New Decision
        </button>

        <div className="glass-card p-6">
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Your Decision Log
          </h1>
          <p className="text-gray-600">
            Track your decisions and reflect on how they unfold over time.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6" data-testid="filter-buttons">
        <button
          onClick={() => setFilter('ALL')}
          className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
            filter === 'ALL'
              ? 'bg-purple-500 text-white'
              : 'bg-white text-gray-600 border-2 border-gray-200 hover:border-purple-300'
          }`}
          data-testid="filter-all"
        >
          All
        </button>
        <button
          onClick={() => setFilter('YES')}
          className={`px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 ${
            filter === 'YES'
              ? 'bg-turquoise-500 text-white'
              : 'bg-white text-gray-600 border-2 border-gray-200 hover:border-turquoise-300'
          }`}
          data-testid="filter-yes"
        >
          <Check className="w-4 h-4" />
          YES
        </button>
        <button
          onClick={() => setFilter('WAIT')}
          className={`px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 ${
            filter === 'WAIT'
              ? 'bg-purple-500 text-white'
              : 'bg-white text-gray-600 border-2 border-gray-200 hover:border-purple-300'
          }`}
          data-testid="filter-wait"
        >
          <Clock className="w-4 h-4" />
          WAIT
        </button>
        <button
          onClick={() => setFilter('NO')}
          className={`px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 ${
            filter === 'NO'
              ? 'bg-pink-500 text-white'
              : 'bg-white text-gray-600 border-2 border-gray-200 hover:border-pink-300'
          }`}
          data-testid="filter-no"
        >
          <X className="w-4 h-4" />
          NO
        </button>
      </div>

      {/* Decisions List */}
      {decisions.length === 0 ? (
        <div className="glass-card p-12 text-center" data-testid="empty-state">
          <p className="text-xl text-gray-500">
            {filter === 'ALL' 
              ? 'No decisions yet. Start by making your first decision!'
              : `No ${filter} decisions found.`
            }
          </p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary mt-6"
          >
            Make a Decision
          </button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="decisions-list">
          {decisions.map((decision) => (
            <div
              key={decision.id}
              className={`glass-card p-6 cursor-pointer hover:shadow-lg transition-shadow border-2 ${getDecisionColor(decision.decision)}`}
              onClick={() => navigate(`/decide/${decision.id}`)}
              data-testid={`decision-card-${decision.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {getDecisionIcon(decision.decision)}
                    <h3 className="text-2xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      {decision.title}
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      decision.decision === 'YES' ? 'bg-turquoise-100 text-turquoise-700' :
                      decision.decision === 'WAIT' ? 'bg-purple-100 text-purple-700' :
                      'bg-pink-100 text-pink-700'
                    }`}>
                      {decision.decision}
                    </span>
                  </div>

                  <p className="text-gray-600 mb-3 line-clamp-2">
                    {decision.original_prompt}
                  </p>

                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {format(new Date(decision.created_at), 'MMM d, yyyy')}
                    </span>
                    {decision.outcome_date && (
                      <span>
                        Outcome: {format(new Date(decision.outcome_date), 'MMM d, yyyy')}
                      </span>
                    )}
                    {decision.revisit_date && (
                      <span>
                        Revisit: {format(new Date(decision.revisit_date), 'MMM d, yyyy')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DecisionLogPage;
